import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner } from 'react-bootstrap'
import Select from 'react-select'
import type { FormatOptionLabelMeta, SingleValue } from 'react-select'
import { FiAlertCircle, FiCheck, FiCopy, FiLink, FiRefreshCw } from 'react-icons/fi'
import useFetch from '../../../_fetch'
import {
  buildTrackingLink,
  campaignRequiresDestination,
  getCampaignDomains,
  getDestinationError,
} from './link-builder-utils'
import './link-builder.css'

type IPublisher = {
  id: string
  name: string
  url: string
  commission: number
  network: string
  country: string
  publisherId: string
  publisherName: string
  shopUrl: string
  credentialsId: string
}

type PublisherOption = {
  value: string
  label: string
  searchableLabel: string
  network: string
  publisherId: string
  publisherName: string
  countries: string[]
}

type CampaignOption = IPublisher & {
  value: string
  label: string
  searchableLabel: string
}

type CopyStatus = 'idle' | 'copied' | 'error'

function Flag({ country }: { country: string }) {
  if (!country || country.toUpperCase() === 'UNKNOWN') return null

  return (
    <img
      className="link-builder-flag"
      src={`/flags/${country.toLowerCase()}.svg`}
      alt={`${country} flag`}
      title={country}
      onError={(event) => {
        event.currentTarget.hidden = true
      }}
    />
  )
}

function matchesSearch(candidateText: string, input: string) {
  const words = input.trim().toLowerCase().split(/\s+/).filter(Boolean)
  if (!words.length) return true

  const normalizedCandidate = candidateText.toLowerCase()
  return words.every((word) => normalizedCandidate.includes(word))
}

function formatPublisherOption(
  option: PublisherOption,
  meta: FormatOptionLabelMeta<PublisherOption>,
) {
  if (meta.context === 'value') {
    return (
      <div className="link-builder-selected-value">
        <span className="fw-semibold text-truncate">{option.publisherName}</span>
        <span className="text-muted text-truncate">{option.network}</span>
      </div>
    )
  }

  return (
    <div className="link-builder-select-option">
      <div className="link-builder-select-option-main">
        <span className="link-builder-option-title">{option.publisherName}</span>
        <span className="link-builder-option-meta">{option.network}</span>
      </div>
      {!!option.countries.length && (
        <div className="link-builder-country-list" aria-label={`Markets: ${option.countries.join(', ')}`}>
          {option.countries.map((country) => (
            <span className="link-builder-country" key={country}>
              <Flag country={country} />
              <span>{country}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

function formatCampaignOption(
  option: CampaignOption,
  meta: FormatOptionLabelMeta<CampaignOption>,
) {
  if (meta.context === 'value') {
    return (
      <div className="link-builder-selected-value">
        <span className="fw-semibold text-truncate">{option.name}</span>
        <span className="text-muted text-truncate">
          {option.country && option.country.toUpperCase() !== 'UNKNOWN' ? option.country : option.network}
        </span>
      </div>
    )
  }

  return (
    <div className="link-builder-select-option">
      <div className="link-builder-select-option-main">
        <span className="link-builder-option-title">
          <Flag country={option.country} />
          <span>{option.name}</span>
        </span>
        {Number(option.commission) > 0 && (
          <span className="link-builder-commission">{Number(option.commission).toFixed(2)}%</span>
        )}
      </div>
      <div className="link-builder-option-meta">
        {option.network} · {option.publisherName}
      </div>
    </div>
  )
}

async function copyText(text: string) {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text)
      return
    } catch {
      // Fall back to the selection-based copy method below.
    }
  }

  const textarea = document.createElement('textarea')
  textarea.value = text
  textarea.setAttribute('readonly', '')
  textarea.style.position = 'fixed'
  textarea.style.opacity = '0'
  document.body.appendChild(textarea)
  textarea.select()

  try {
    if (!document.execCommand('copy')) throw new Error('Copy failed')
  } finally {
    document.body.removeChild(textarea)
  }
}

export default function LinkBuilder() {
  const { doFetch } = useFetch()

  const [publishers, setPublishers] = useState<IPublisher[]>([])
  const [selectedPublisher, setSelectedPublisher] = useState<PublisherOption | null>(null)
  const [selectedCampaign, setSelectedCampaign] = useState<CampaignOption | null>(null)
  const [clickReference, setClickReference] = useState('')
  const [deepLink, setDeepLink] = useState('')
  const [generatedResult, setGeneratedResult] = useState('')
  const [generationError, setGenerationError] = useState('')
  const [hasAttemptedGeneration, setHasAttemptedGeneration] = useState(false)
  const [copyStatus, setCopyStatus] = useState<CopyStatus>('idle')
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const copyResetTimer = useRef<number | null>(null)

  const fetchPublishers = useCallback(async () => {
    setIsLoading(true)
    setLoadError('')

    try {
      const response = await doFetch('/publisher-campaigns/all')
      if (!response.ok) {
        throw new Error(`Campaigns could not be loaded (${response.status}).`)
      }

      const data = await response.json()
      if (!Array.isArray(data)) throw new Error('The campaign list returned an unexpected response.')

      setPublishers(data as IPublisher[])
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : 'Campaigns could not be loaded.')
    } finally {
      setIsLoading(false)
    }
  }, [doFetch])

  useEffect(() => {
    void fetchPublishers()
  }, [fetchPublishers])

  useEffect(() => () => {
    if (copyResetTimer.current !== null) window.clearTimeout(copyResetTimer.current)
  }, [])

  const publisherOptions = useMemo<PublisherOption[]>(() => {
    const grouped = new Map<string, PublisherOption>()

    publishers.forEach((publisher) => {
      const key = JSON.stringify([publisher.network, publisher.publisherId])
      const existing = grouped.get(key)
      const normalizedCountry = String(publisher.country || '').trim()

      if (!existing) {
        const publisherName = String(publisher.publisherName || publisher.publisherId || 'Unknown publisher')
        grouped.set(key, {
          value: key,
          label: `${publisherName} ${publisher.network}`,
          searchableLabel: `${publisher.network} ${publisherName} ${normalizedCountry}`,
          network: publisher.network,
          publisherId: publisher.publisherId,
          publisherName,
          countries: normalizedCountry ? [normalizedCountry] : [],
        })
        return
      }

      if (normalizedCountry && !existing.countries.includes(normalizedCountry)) {
        existing.countries.push(normalizedCountry)
        existing.searchableLabel += ` ${normalizedCountry}`
      }
    })

    return Array.from(grouped.values())
      .map((option) => ({ ...option, countries: [...option.countries].sort() }))
      .sort((first, second) => (
        first.publisherName.localeCompare(second.publisherName)
        || first.network.localeCompare(second.network)
      ))
  }, [publishers])

  const campaignOptions = useMemo<CampaignOption[]>(() => {
    const visiblePublishers = selectedPublisher
      ? publishers.filter((publisher) => (
        publisher.network === selectedPublisher.network
        && publisher.publisherId === selectedPublisher.publisherId
      ))
      : publishers

    return visiblePublishers
      .map((publisher) => ({
        ...publisher,
        value: JSON.stringify([
          publisher.network,
          publisher.publisherId,
          publisher.id,
          publisher.country,
        ]),
        label: publisher.name,
        searchableLabel: [
          publisher.name,
          publisher.country,
          publisher.network,
          publisher.publisherName,
          ...getCampaignDomains(publisher.shopUrl),
          Number(publisher.commission) > 0 ? Number(publisher.commission).toFixed(2) : '',
        ].join(' '),
      }))
      .sort((first, second) => (
        first.name.localeCompare(second.name)
        || first.country.localeCompare(second.country)
        || first.network.localeCompare(second.network)
      ))
  }, [publishers, selectedPublisher])

  const requiresDestination = campaignRequiresDestination(selectedCampaign)
  const validDomains = useMemo(
    () => getCampaignDomains(selectedCampaign?.shopUrl || ''),
    [selectedCampaign],
  )
  const primaryDomain = validDomains.find((domain) => !domain.startsWith('*.'))
    || validDomains[0]?.replace(/^\*\./, '')
  const destinationError = hasAttemptedGeneration && selectedCampaign && requiresDestination
    ? getDestinationError(deepLink, true)
    : ''

  const invalidateGeneratedLink = useCallback(() => {
    setGeneratedResult('')
    setGenerationError('')
    setCopyStatus('idle')
  }, [])

  const handlePublisherChange = (option: SingleValue<PublisherOption>) => {
    setSelectedPublisher(option)
    setSelectedCampaign(null)
    setDeepLink('')
    setHasAttemptedGeneration(false)
    invalidateGeneratedLink()
  }

  const handleCampaignChange = (option: SingleValue<CampaignOption>) => {
    setSelectedCampaign(option)
    setDeepLink('')
    setHasAttemptedGeneration(false)
    invalidateGeneratedLink()
  }

  const handleGenerate = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!selectedCampaign) return

    setHasAttemptedGeneration(true)
    setGenerationError('')
    setCopyStatus('idle')

    const result = buildTrackingLink(selectedCampaign, deepLink, clickReference)
    if (result.error) {
      setGeneratedResult('')
      if (result.error !== getDestinationError(deepLink, requiresDestination)) {
        setGenerationError(result.error)
      }
      return
    }

    setGeneratedResult(result.link)
  }

  const handleCopy = async () => {
    if (!generatedResult) return

    try {
      await copyText(generatedResult)
      setCopyStatus('copied')
      if (copyResetTimer.current !== null) window.clearTimeout(copyResetTimer.current)
      copyResetTimer.current = window.setTimeout(() => setCopyStatus('idle'), 2400)
    } catch {
      setCopyStatus('error')
    }
  }

  return (
    <div className="link-builder-page">
      <div className="link-builder-page-header">
        <h3 className="mb-1">Link Builder</h3>
        <p className="text-muted mb-0">
          Create a campaign tracking link for articles, ads and other placements.
        </p>
      </div>

      {!!loadError && (
        <Alert variant="danger" className="d-flex flex-column flex-sm-row align-items-sm-center justify-content-between gap-3">
          <span className="d-flex align-items-start gap-2">
            <FiAlertCircle className="flex-shrink-0 mt-1" />
            <span>{loadError}</span>
          </span>
          <Button variant="outline-danger" size="sm" onClick={() => void fetchPublishers()}>
            <FiRefreshCw className="me-2" />
            Try again
          </Button>
        </Alert>
      )}

      {!isLoading && !loadError && publishers.length === 0 && (
        <Alert variant="info">
          No campaigns are available for this account yet. Add or activate a campaign before building a link.
        </Alert>
      )}

      <Form noValidate onSubmit={handleGenerate}>
        <Card className="link-builder-card shadow-sm border-0 mb-4">
          <Card.Body className="p-3 p-md-4">
            <Row className="g-3">
              <Col xs={12} lg={6} className="link-builder-grid-column">
                <Form.Group controlId="publisher-select">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <Form.Label className="mb-0">Publisher account</Form.Label>
                    <Badge bg="light" text="secondary" className="border fw-normal">Optional</Badge>
                  </div>
                  <Select<PublisherOption, false>
                    inputId="publisher-select"
                    instanceId="link-builder-publisher-select"
                    className="link-builder-select"
                    classNamePrefix="link-builder-select"
                    value={selectedPublisher}
                    options={publisherOptions}
                    getOptionLabel={(option) => option.label}
                    getOptionValue={(option) => option.value}
                    formatOptionLabel={formatPublisherOption}
                    placeholder="All publisher accounts"
                    isClearable
                    isSearchable
                    isLoading={isLoading}
                    isDisabled={isLoading || !publisherOptions.length}
                    menuPlacement="auto"
                    maxMenuHeight={320}
                    onChange={handlePublisherChange}
                    filterOption={(candidate, input) => matchesSearch(candidate.data.searchableLabel, input)}
                    loadingMessage={() => 'Loading publisher accounts…'}
                    noOptionsMessage={({ inputValue }) => (
                      inputValue ? `No publisher accounts match “${inputValue}”` : 'No publisher accounts available'
                    )}
                  />
                </Form.Group>
              </Col>

              <Col xs={12} lg={6} className="link-builder-grid-column">
                <Form.Group controlId="campaign-select">
                  <Form.Label className="mb-2">
                    Campaign <span className="text-danger" aria-hidden="true">*</span>
                  </Form.Label>
                  <Select<CampaignOption, false>
                    inputId="campaign-select"
                    instanceId="link-builder-campaign-select"
                    className="link-builder-select"
                    classNamePrefix="link-builder-select"
                    value={selectedCampaign}
                    options={campaignOptions}
                    getOptionLabel={(option) => option.label}
                    getOptionValue={(option) => option.value}
                    formatOptionLabel={formatCampaignOption}
                    placeholder={selectedPublisher ? 'Select a campaign' : 'Search all campaigns'}
                    required
                    isClearable
                    isSearchable
                    isLoading={isLoading}
                    isDisabled={isLoading || !campaignOptions.length}
                    menuPlacement="auto"
                    maxMenuHeight={320}
                    onChange={handleCampaignChange}
                    filterOption={(candidate, input) => matchesSearch(candidate.data.searchableLabel, input)}
                    loadingMessage={() => 'Loading campaigns…'}
                    noOptionsMessage={({ inputValue }) => (
                      inputValue ? `No campaigns match “${inputValue}”` : 'No campaigns available'
                    )}
                  />
                </Form.Group>
              </Col>
            </Row>

            {selectedCampaign && (
              <div className="link-builder-campaign-summary" aria-live="polite">
                <div className="link-builder-campaign-summary-top">
                  <div className="link-builder-campaign-summary-main">
                    <Flag country={selectedCampaign.country} />
                    <div className="min-width-0">
                      <div className="fw-semibold text-break">{selectedCampaign.name}</div>
                      <div className="text-muted small text-break">
                        {selectedCampaign.network} · {selectedCampaign.publisherName}
                      </div>
                    </div>
                  </div>
                  <div className="d-flex flex-wrap gap-2">
                    {selectedCampaign.country && selectedCampaign.country.toUpperCase() !== 'UNKNOWN' && (
                      <Badge bg="light" text="dark" className="border fw-normal">{selectedCampaign.country}</Badge>
                    )}
                    {Number(selectedCampaign.commission) > 0 && (
                      <Badge bg="light" text="dark" className="border fw-normal">
                        {Number(selectedCampaign.commission).toFixed(2)}% commission
                      </Badge>
                    )}
                  </div>
                </div>

                {!!validDomains.length && (
                  <div className="link-builder-domain-section">
                    <div className="link-builder-domain-heading">
                      <span>Valid domains</span>
                      <span>{validDomains.length}</span>
                    </div>
                    <div className="link-builder-domain-list" aria-label="Valid domains for this campaign">
                      {validDomains.slice(0, 6).map((domain) => (
                        <span className="link-builder-domain" key={domain} title={domain.startsWith('*.') ? 'Includes subdomains' : domain}>
                          {domain}
                        </span>
                      ))}
                    </div>
                    {validDomains.length > 6 && (
                      <details className="link-builder-more-domains">
                        <summary>Show {validDomains.length - 6} more</summary>
                        <div className="link-builder-domain-list mt-2">
                          {validDomains.slice(6).map((domain) => (
                            <span className="link-builder-domain" key={domain} title={domain.startsWith('*.') ? 'Includes subdomains' : domain}>
                              {domain}
                            </span>
                          ))}
                        </div>
                      </details>
                    )}
                  </div>
                )}
              </div>
            )}

            <h6 className="link-builder-details-heading">Link details</h6>

            <Row className="g-3 align-items-start">
              <Col xs={12} lg={8} className="link-builder-grid-column">
                <Form.Group controlId="deep-link">
                  <Form.Label>
                    Destination URL
                    {requiresDestination && <span className="text-danger ms-1" aria-hidden="true">*</span>}
                  </Form.Label>
                  <Form.Control
                    type="text"
                    inputMode="url"
                    autoComplete="url"
                    placeholder={primaryDomain ? `https://${primaryDomain}/page` : 'https://example.com/article-or-product'}
                    value={deepLink}
                    disabled={!selectedCampaign}
                    required={requiresDestination}
                    isInvalid={!!destinationError}
                    aria-describedby={destinationError ? 'deep-link-feedback' : 'deep-link-help'}
                    onChange={(event) => {
                      setDeepLink(event.target.value)
                      invalidateGeneratedLink()
                    }}
                  />
                  <Form.Control.Feedback id="deep-link-feedback" type="invalid">
                    {destinationError}
                  </Form.Control.Feedback>
                  {!destinationError && (
                    <Form.Text id="deep-link-help" muted>
                      {selectedCampaign && !requiresDestination
                        ? 'This campaign uses a fixed destination, so this field will not change the generated link.'
                        : 'Paste the page visitors should land on. A missing https:// prefix is added automatically for Nordpar links.'}
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>

              <Col xs={12} lg={4} className="link-builder-grid-column">
                <Form.Group controlId="click-reference">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <Form.Label className="mb-0">Click reference</Form.Label>
                    <Badge bg="light" text="secondary" className="border fw-normal">Optional</Badge>
                  </div>
                  <Form.Control
                    type="text"
                    placeholder="e.g. article-hero"
                    value={clickReference}
                    disabled={!selectedCampaign}
                    aria-describedby="click-reference-help"
                    onChange={(event) => {
                      setClickReference(event.target.value)
                      invalidateGeneratedLink()
                    }}
                  />
                  <Form.Text id="click-reference-help" muted>
                    Use a placement name to compare clicks later.
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            {!!generationError && (
              <Alert variant="danger" className="mt-3 mb-0">
                <FiAlertCircle className="me-2" />
                {generationError}
              </Alert>
            )}

            <div className="link-builder-generate-row">
              {!selectedCampaign && (
                <div className="text-muted small">Select a campaign to enable link generation.</div>
              )}
              <Button type="submit" disabled={!selectedCampaign || isLoading} className="link-builder-generate-button">
                <FiLink className="me-2" />
                Generate link
              </Button>
            </div>
          </Card.Body>
        </Card>
      </Form>

      {generatedResult && (
        <Card className="link-builder-card shadow-sm border-0">
          <Card.Header className="bg-white border-0 px-3 px-md-4 pt-4 pb-0">
            <h5 className="mb-1">Generated link</h5>
            <p className="text-muted small mb-0">Copy the finished URL into your content or campaign.</p>
          </Card.Header>
          <Card.Body className="p-3 p-md-4">
            <>
              <div className="link-builder-result-layout">
                <Form.Control
                  as="textarea"
                  rows={3}
                  className="link-builder-result"
                  value={generatedResult}
                  readOnly
                  aria-label="Generated tracking link"
                  onFocus={(event) => event.currentTarget.select()}
                />
                <Button
                  variant={copyStatus === 'copied' ? 'success' : 'outline-secondary'}
                  className="link-builder-copy-button"
                  onClick={() => void handleCopy()}
                >
                  {copyStatus === 'copied' ? <FiCheck className="me-2" /> : <FiCopy className="me-2" />}
                  {copyStatus === 'copied' ? 'Copied' : 'Copy link'}
                </Button>
              </div>

              <div className="link-builder-copy-status small mt-2" aria-live="polite">
                {copyStatus === 'copied' && <span className="text-success">Link copied to your clipboard.</span>}
                {copyStatus === 'error' && (
                  <span className="text-danger">The link could not be copied. Select the link and copy it manually.</span>
                )}
              </div>
            </>
          </Card.Body>
        </Card>
      )}

      {isLoading && (
        <div className="link-builder-loading" role="status" aria-live="polite">
          <Spinner animation="border" size="sm" />
          <span>Loading campaigns…</span>
        </div>
      )}
    </div>
  )
}
